import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, Renderer2, ViewChild } from '@angular/core';
import { OrgBusinessUnit, OrgBusinessUnitOptions } from '../../org-business-units/org-business-unit.model';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { ActionMenuLocation } from '../action-menu/action-menu.component';
import { cloneDeep } from 'lodash-es';
import { concat, Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil, tap } from 'rxjs/operators';
import { NgSelectComponent } from '@ng-select/ng-select';
import { GlobalEventListenerService } from '../../services/global-event-listener.service';

interface NestedBusinessUnit {
  unit: OrgBusinessUnit;
  selectable?: boolean;
  inactive?: boolean;
  children?: NestedBusinessUnit[];
}

@Component({
    selector: 'topo-business-unit-select',
    templateUrl: './business-unit-select.component.html',
    styleUrls: ['./business-unit-select.component.scss'],
    standalone: false
})
export class BusinessUnitSelectComponent implements OnDestroy {
  @Input() set businessUnits(value: OrgBusinessUnit[]) {
    this._businessUnits = value;
    if (!!this._businessUnits && !!this._selectedUIds) {
      this.loadTreeData();
    }
  }

  @Input() set selectedUIds(value: string[]) {
    this._selectedUIds = value;
    if (!!this._businessUnits && !!this._selectedUIds) {
      this.loadTreeData();
    }
  }

  @Input() multiSelectionMode: boolean;
  @Input() readonly: boolean;
  @Input() location = 'left';
  @Input() skinny = false;
  @Input() disabled = false;
  @Input() alignBottom = false;
  @Input() alignRight = false;
  @Input() searchPlaceholder: string;
  @Input() flowMode: boolean = true;
  @Input() theme: 'default' | 'purple' = 'default';
  @Output() selectedChanged = new EventEmitter<string[]>();

  @ViewChild('tree') tree: any;
  @ViewChild('menu') menu: ElementRef;
  @ViewChild('ngSelect') ngSelect: NgSelectComponent;

  constructor(
    private renderer: Renderer2,
    private eventListenerService: GlobalEventListenerService
  ) {}

  isActive = false;
  _businessUnits: OrgBusinessUnit[];
  _selectedUIds: string[];
  rootUId: string;
  rootSecured: boolean;
  treeControl: any;
  dataSource: any;
  destroy$ = new Subject<void>();
  noIconClass = '';
  selectableUnits: OrgBusinessUnit[] = [];
  nestedUnits: NestedBusinessUnit[] = [];
  private nestedUnitsDone: string[] = [];

  units$: Observable<OrgBusinessUnit[]>;
  unitsLoading = false;
  unitsInput$ = new Subject<string>();
  selectedUnits: OrgBusinessUnit[] = [];
  clickSubscription: Subscription = null;

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.unsubscribeToClicks();
  }

  unitsTrackByFn(unit: OrgBusinessUnit) {
    return unit?.uId;
  }

  onUnitSearchChanged(unit: OrgBusinessUnit): void {
    if (!this.multiSelectionMode) {
      this.selectedChanged.emit([unit.uId]);
      this.closeMenu();
    } else {
      let selectedUIds = cloneDeep(this._selectedUIds);

      if (!!!selectedUIds) {
        selectedUIds = [];
      }

      if (!!!unit.parentUId) {
        this.rootUId = unit.uId;
        selectedUIds = [unit.uId];
        this.selectedChanged.emit(selectedUIds);
        return;
      } else if (!!this.rootUId) {
        const index = selectedUIds.indexOf(this.rootUId);
        if (index > -1) {
          selectedUIds.splice(index);
        }
      }
      const selectedIndex = selectedUIds?.findIndex(n => n === unit.uId);
      if (selectedIndex == -1) {
        selectedUIds.push(unit.uId);
      }

      if (selectedUIds.length == 0) {
        selectedUIds = [this.rootUId];
      }

      this.selectedChanged.emit(selectedUIds);
    }
  }

  private loadTreeData(): void {
    this.nestedUnits = [];
    this.nestedUnitsDone = [];
    this.selectableUnits = this._businessUnits?.filter(
      u => !!!u.options || !((u.options & OrgBusinessUnitOptions.NotSelectable) === OrgBusinessUnitOptions.NotSelectable)
    );
    const rootUnit = this._businessUnits.find(u => !!!u.parentUId);
    if (!!rootUnit) {
      this.rootUId = rootUnit.uId;
      this.rootSecured = !!rootUnit.securityGroupUIds?.[0];
      const nestedRoot = { unit: rootUnit, selectable: !this.rootSecured || this.selectableUnits.some(u => u.uId === this.rootUId), children: [] };
      this.nestedUnitsDone.push(rootUnit.uId);
      this.nestedUnits.push(nestedRoot);
      this.createNestedUnits(nestedRoot);
    }
    this.treeControl = new NestedTreeControl<NestedBusinessUnit>(node => node.children);
    this.dataSource = new MatTreeNestedDataSource<NestedBusinessUnit>();
    this.dataSource.data = this.nestedUnits;
    this.treeControl.dataNodes = this.nestedUnits;

    var rootNode = this.treeControl.dataNodes[0];
    if (!!this._selectedUIds) {
      if (!this.multiSelectionMode) {
        if (this._selectedUIds.length == 1) {
          if (this._selectedUIds[0] === rootNode.unit.uId) {
            this.treeControl.expand(rootNode);
          } else {
            this.expandToNode(rootNode, this._selectedUIds[0]);
          }
        }
      } else {
        this.treeControl.expandAll();
      }
    }

    this.unitsLoading = false;
    this.selectedUnits = [];
    const obsArr: Array<Observable<OrgBusinessUnit[]>> = [];

    obsArr.push(
      this.unitsInput$.pipe(
        tap(() => (this.unitsLoading = true)),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(searchTerm => this.searchUnits(searchTerm))
      )
    );

    this.units$ = concat(...obsArr);
  }

  private searchUnits(searchTerm: string): Observable<OrgBusinessUnit[]> {
    this.unitsLoading = false;
    this.selectedUnits = [];
    if (!!!searchTerm?.length) {
      return of([]);
    }
    const search = searchTerm.toLocaleLowerCase();
    const firstResults = this.selectableUnits?.filter(u => u.name.toLocaleLowerCase().startsWith(search));
    const secondResults = this.selectableUnits?.filter(
      u => !u.name.toLocaleLowerCase().startsWith(search) && u.name.toLocaleLowerCase().includes(search)
    );
    const results = [...firstResults, ...secondResults];
    results.forEach(r => {
      const pathToRoot = [];
      const foundParents = [];
      let searchUId = r.uId;
      do {
        const searchUnit = this._businessUnits.find(u => u.uId === searchUId);
        if (!!searchUnit && !!searchUnit?.parentUId) {
          pathToRoot.push(searchUnit.name);
        }
        searchUId = searchUnit?.parentUId;
        if (!!searchUId && !foundParents.includes(searchUId)) {
          foundParents.push(searchUId);
        }
        else {
          searchUId = null;
        }
      } while (!!searchUId);

      pathToRoot.reverse();
      r.path = pathToRoot.join(' / ');
    });
    return of(results);
  }

  hasChild = (_: number, node: NestedBusinessUnit) => !!node.children && node.children.length > 0;

  private createNestedUnits(parentUnit: NestedBusinessUnit) {
    this._businessUnits
      ?.filter(u => u.parentUId === parentUnit.unit.uId)
      .forEach(u => {
        const notSelectable = !!u.options && (u.options & OrgBusinessUnitOptions.NotSelectable) === OrgBusinessUnitOptions.NotSelectable;
        const nested = { unit: u, selectable: !notSelectable, children: [], inactive: u.userHasNoAccess === true };
        this.nestedUnitsDone.push(u.uId);
        parentUnit.children.push(nested);
        this.createNestedUnits(nested);
      });
    parentUnit.children = parentUnit.children.sort((a, b) =>
      a.unit.name.toLocaleLowerCase().localeCompare(b.unit.name.toLocaleLowerCase())
    );
  }

  private expandToNode(node: NestedBusinessUnit, selectedNodeUId: string): boolean {
    if (node.unit?.uId === selectedNodeUId) {
      return true;
    }
    this.treeControl.expand(node);
    let found = false;
    node.children?.forEach(c => {
      if (this.expandToNode(c, selectedNodeUId) === true) {
        found = true;
        return;
      }
    });

    if (!found) {
      this.treeControl.collapse(node);
    }
    return found;
  }

  private subscribeToClicks() {
    if (this.clickSubscription) {
      return;
    }

    this.clickSubscription = this.eventListenerService.clickEvents$.pipe(takeUntil(this.destroy$)).subscribe(target => {
      const menuElm = this.menu?.nativeElement;
      if (!!menuElm && !menuElm.contains(target, menuElm)) {
        if (target.className !== 'checkbox-input-wrapper' && target.className !== 'checkbox-input-wrapper checked') {
          this.closeMenu();
        }
      }
    });
  }

  private unsubscribeToClicks() {
    if (this.clickSubscription) {
      this.clickSubscription.unsubscribe();
      this.clickSubscription = null;
    }
  }

  public isNodeSelected(node: NestedBusinessUnit): boolean {
    return this._selectedUIds?.includes(node.unit.uId);
  }

  public onSelectNode(node: NestedBusinessUnit): void {
    if (node.selectable !== true || node.inactive === true) {
      return;
    }

    let selectedUIds = cloneDeep(this._selectedUIds);

    if (!!!selectedUIds) {
      selectedUIds = [];
    }

    if (this.multiSelectionMode !== true) {
      selectedUIds = [];
    } else {
      if (!!!node.unit.parentUId) {
        this.rootUId = node.unit.uId;
        this.rootSecured = !!node.unit.securityGroupUIds?.[0];
        if (this.rootSecured !== true) {
          selectedUIds = [node.unit.uId];
          this.selectedChanged.emit(selectedUIds);
          return;
        }
      } else if (!!this.rootUId && this.rootSecured !== true) {
        const index = selectedUIds.indexOf(this.rootUId);
        if (index > -1) {
          selectedUIds.splice(index);
        }
      }
    }
    const selectedIndex = selectedUIds?.findIndex(n => n === node.unit.uId);
    if (selectedIndex != -1) {
      selectedUIds.splice(selectedIndex, 1);
    } else {
      selectedUIds.push(node.unit.uId);
    }

    if (this.multiSelectionMode === true && selectedUIds.length == 0) {
      if (this.rootSecured !== true) {
        selectedUIds = [this.rootUId];
      }
    }

    this.selectedChanged.emit(selectedUIds);

    if (!this.multiSelectionMode) {
      this.closeMenu();
    }
  }

  toggleMenu(): void {
    if (this.isActive) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  closeMenu(): void {
    const menuElm = this.menu?.nativeElement;
    if (!!!menuElm) {
      return;
    }
    menuElm.classList.remove('is-active');
    this.unsubscribeToClicks();
  }

  openMenu(): void {
    const menuElm = this.menu?.nativeElement;
    if (!!!menuElm) {
      return;
    }
    if (!!!this.tree) {
      return;
    }

    setTimeout(() => {
      this.ngSelect?.focus();
    }, 10);

    const ddMenuElm = this.tree?.nativeElement;
    if (!!!ddMenuElm) {
      return;
    }

    menuElm.classList.add('is-active');
    this.subscribeToClicks();

    const { x, y } = this.getMenuPosition(menuElm, ddMenuElm);

    this.renderer.setStyle(this.tree.nativeElement, 'top', `${y}px`);
    this.renderer.setStyle(this.tree.nativeElement, 'left', `${x}px`);

    this.isActive = true;
    this.loadTreeData();
  }

  private getMenuPosition(elm, ddMenuElm, coordinates?: { x: number; y: number }): { x: number; y: number } {
    const { top, left, width, height } = elm.getBoundingClientRect();

    let verticalSizeOffset = 45; //this.size === ActionMenuSize.normal || this.size === ActionMenuSize.large ? 45 : 30;
    verticalSizeOffset = coordinates ? verticalSizeOffset : 0;

    const menuList = ddMenuElm.getBoundingClientRect();

    let y = top + window.scrollY + verticalSizeOffset;
    let x = left + window.scrollX;

    // When given specific coordinates, just render there
    if (coordinates) {
      y = coordinates.y;
      x = coordinates.x;
    }

    if (this.location === ActionMenuLocation.left) {
      x = x - (this.skinny ? 80 : menuList.width);
    }

    // Move the edge of the menu by the button width/height to allow more alignment options
    if (this.alignBottom) {
      y = y + height;
    }

    if (this.alignRight) {
      x = x + width;
    }

    // make sure we have enough room to show the menu below the
    // menu button. If not, then show it above the button
    const menuHeight = menuList.bottom - menuList.top;

    if (y + menuHeight > window.innerHeight) {
      y = top - menuHeight;
    }

    return { x, y };
  }
}
